import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
  CalendarDaysIcon,
  UserIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useGetTasksQuery, useGetActiveUsersQuery, useAddTaskMutation, useUpdateTaskMutation, useDeleteTaskMutation } from '../store/api';
import ExportButton from '../components/ExportButton';
import PermissionButton from '../components/PermissionButton';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import { useAuth, usePermission } from '../hooks/useAuth';

export default function TaskManagement() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [dayTasks, setDayTasks] = useState([]);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);

  // Auth hooks - strictly permission-based
  const { user } = useAuth();
  const canAssignToOthers = usePermission('task_management.assign_others'); // Check if user can assign tasks to others
  const canAddTask = usePermission('task_management.add');
  const canEditTask = usePermission('task_management.edit');
  const canDeleteTask = usePermission('task_management.delete');
  const canViewTask = usePermission('task_management.view');

  // Utility function to format date as local string (YYYY-MM-DD)
  const formatLocalDate = (date) => {
    if (!date || !(date instanceof Date)) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    start_date: '',
    priority: '',
    notes: '',
    status: 'pending'
  });

  // Get current month's start and end dates
  const getMonthRange = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    return {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate)
    };
  };

  const monthRange = getMonthRange();

  // Get Tasks Query for current month - load ALL tasks for accurate statistics
  const { data, isError, error, isSuccess, isLoading } = useGetTasksQuery({
    // Remove date filtering to get all tasks for accurate statistics
    is_export: isExport,
    page: 1,
    limit: 10000 // Increase limit to ensure all tasks are loaded
  });

  // Get Users for assignment
  const { data: usersData } = useGetActiveUsersQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  const [tasksData, setTasksData] = useState([]);

  useEffect(() => {
    if (isExport == 1 && data?.data) {
      const transformedData = data.data.map(task => ({
        'Başlıq': task.title || '',
        'Təsvir': task.description || '',
        'Təyin olunmuş': task.assigned_user?.first_name + ' ' + task.assigned_user?.last_name || '',
        'Başlama tarixi': task.start_date || '',
        'Bitmə tarixi': task.due_date || '',
        'Prioritet': task.priority || '',
        'Status': task.status || '',
        'Qeydlər': task.notes || '',
        'ID': task.id
      }));
      setExportData(transformedData);
    } else {
      if (!isLoading && data?.data) {
        // Handle nested data structure from backend
        const tasks = data.data.data || data.data || [];
        setTasksData(tasks);
      }
    }
  }, [data, isSuccess, isError, isLoading, error]);

  // Add Task Mutation
  const [addTask, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddTaskMutation();

  useEffect(() => {
    if (!createIsLoading && createResponse) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setSelectedDate(null);
      resetForm();
    }
    if (createIsError) {
      toast.error(createError?.data?.message || 'Tapşırıq əlavə etmə uğursuz oldu');
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError]);

  // Update Task Mutation
  const [updateTask, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateTaskMutation();

  useEffect(() => {
    if (!updateIsLoading && updateData) {
      toast.success(updateData.message);
      setIsTaskModalOpen(false);
      setEditingTask(null);
      resetForm();
    }
    if (updateIsError) {
      toast.error(updateError?.data?.message || 'Tapşırıq yeniləmə uğursuz oldu');
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError]);

  // Delete Task Mutation
  const [deleteTask, {
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteTaskMutation();

  useEffect(() => {
    if (!deleteIsLoading && deleteRes && deleteIsSuccess) {
      toast.success(deleteRes.message);
    }
    if (deleteIsError && deleteError) {
      toast.error('Tapşırıq silmə əməliyyatı uğursuz oldu');
    }
  }, [deleteIsSuccess, deleteIsError, deleteRes, deleteError, deleteIsLoading]);

  const exportHeaders = [
    'Başlıq', 'Təsvir', 'Təyin olunmuş', 'Başlama tarixi', 'Bitmə tarixi', 'Prioritet', 'Status', 'Qeydlər', 'ID'
  ];

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assigned_to: '', // Assign to self if not super admin
      due_date: '',
      start_date: '',
      priority: '',
      notes: '',
      status: 'pending'
    });
  };

  // Calendar navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Get calendar days
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDateIterator = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDateIterator));
      currentDateIterator.setDate(currentDateIterator.getDate() + 1);
    }
    
    return days;
  };

  // Get tasks for a specific date (filtered by user role)
  const getTasksForDate = (date) => {
    const dateStr = formatLocalDate(date);
    
    // Start with role-filtered tasks (all tasks, not just current month)
    const roleFilteredTasks = getFilteredTasksForStats();
    
    const filteredTasks = roleFilteredTasks.filter(task => {
      // Extract date part from ISO string (e.g., "2025-07-22T00:00:00.000000Z" -> "2025-07-22")
      const taskDueDate = task.due_date ? task.due_date.split('T')[0] : null;
      const taskStartDate = task.start_date ? task.start_date.split('T')[0] : null;
      
      const matches = taskDueDate === dateStr || taskStartDate === dateStr;
      
      return matches;
    });
    
    return filteredTasks;
  };

  // Get task counts by priority for a date
  const getTaskCountsByPriority = (date) => {
    const tasks = getTasksForDate(date);
    const overdueTasks = tasks.filter(t => 
      isTaskOverdue(t.due_date) && 
      t.status !== 'completed' && 
      t.status !== 'cancelled'
    );
    
    const counts = {
      high: tasks.filter(t => t.priority === 'high').length,
      medium: tasks.filter(t => t.priority === 'medium').length,
      low: tasks.filter(t => t.priority === 'low').length,
      overdue: overdueTasks.length,
      total: tasks.length
    };
    
    return counts;
  };

  const handleAdd = (forDate = null) => {
    setEditingTask(null);
    resetForm();
    const dateToUse = forDate || selectedDate;
    
    // Only set due_date if we have a valid date
    let dueDateValue = '';
    if (dateToUse && dateToUse instanceof Date) {
      dueDateValue = formatLocalDate(dateToUse);
    }
    
    const initialFormData = {
      title: '',
      description: '',
      assigned_to: '', // Assign to self if not super admin
      due_date: dueDateValue,
      start_date: '',
      priority: '',
      notes: '',
      status: 'pending'
    };
    
    setFormData(initialFormData);
    setIsModalOpen(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '', // Assign to self if not super admin
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      start_date: task.start_date ? task.start_date.split('T')[0] : '',
      priority: task.priority || '',
      notes: task.notes || '',
      status: task.status || 'pending'
    });
    setIsTaskModalOpen(true);
  };

  const handleDelete = (taskId) => {
    if (window.confirm('Bu tapşırığı silmək istədiyinizə əminsiniz?')) {
      deleteTask(taskId);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Enforce role-based assignment: non-super admins can only assign to themselves
    const assignedTo = canAssignToOthers ? 
      (parseInt(formData.assigned_to) || null) : 
      (parseInt(user?.id) || null);
    
    const submitData = {
      ...formData,
      assigned_to: assignedTo
    };

    if (editingTask) {
      updateTask({ id: editingTask.id, obj: submitData });
    } else {
      addTask(submitData);
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const tasks = getTasksForDate(date);
    setDayTasks(tasks);
    setIsDayModalOpen(true);
  };

  const handleDateHover = (date) => {
    setHoveredDate(date);
  };

  const getPriorityColor = (priority) => {
    const priorityMap = {
      'low': 'bg-green-500',
      'medium': 'bg-yellow-500',
      'high': 'bg-red-500'
    };
    return priorityMap[priority] || 'bg-gray-400';
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in_progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusText = (status) => {
    const textMap = {
      'pending': 'Gözləmədə',
      'in_progress': 'Davam edir',
      'completed': 'Tamamlandı',
      'cancelled': 'Ləğv edildi'
    };
    return textMap[status] || status;
  };

  const getPriorityText = (priority) => {
    const textMap = {
      'low': 'Az',
      'medium': 'Orta',
      'high': 'Yüksək'
    };
    return textMap[priority] || priority;
  };

  // Check if task is overdue
  const isTaskOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const taskDueDate = new Date(dueDate);
    
    // Set time to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    taskDueDate.setHours(0, 0, 0, 0);
    
    return taskDueDate < today;
  };

  const calendarDays = getCalendarDays();
  const monthNames = [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun',
    'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'
  ];

  const dayNames = ['Bazar', 'Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə'];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate statistics based on user role (for ALL tasks)
  const getFilteredTasksForStats = () => {
    if (!tasksData || tasksData.length === 0) return [];
    
    if (canAssignToOthers) {
      return tasksData; // Super admin sees all tasks
    } else {
      return tasksData.filter(task => task.assigned_to === user?.id); // Regular user sees only their tasks
    }
  };

  const filteredTasksForStats = getFilteredTasksForStats();
  const totalTasks = filteredTasksForStats.length;
  const pendingTasks = filteredTasksForStats.filter(t => t.status === 'pending').length;
  const inProgressTasks = filteredTasksForStats.filter(t => t.status === 'in_progress').length;
  const completedTasks = filteredTasksForStats.filter(t => t.status === 'completed').length;
  const highPriorityTasks = filteredTasksForStats.filter(t => t.priority === 'high').length;

  // Statistics are calculated and ready for display

  // Separate function for calendar filtering (current month + role-based)
  const getFilteredTasksForCalendar = () => {
    if (!tasksData || tasksData.length === 0) return [];
    
    // First apply role-based filtering
    const roleFiltered = canAssignToOthers ? tasksData : tasksData.filter(task => task.assigned_to === user?.id);
    
    // Then filter by current month for calendar display
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    return roleFiltered.filter(task => {
      const taskDueDate = task.due_date ? new Date(task.due_date) : null;
      const taskStartDate = task.start_date ? new Date(task.start_date) : null;
      
      // Check if task's due date or start date is in current month
      const isDueDateInMonth = taskDueDate && 
        taskDueDate.getFullYear() === currentYear && 
        taskDueDate.getMonth() === currentMonth;
        
      const isStartDateInMonth = taskStartDate && 
        taskStartDate.getFullYear() === currentYear && 
        taskStartDate.getMonth() === currentMonth;
        
      return isDueDateInMonth || isStartDateInMonth;
    });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">Tapşırıq Təqvimi</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              canAssignToOthers 
                ? 'bg-red-100 text-red-800 border border-red-200' 
                : 'bg-blue-100 text-blue-800 border border-blue-200'
            }`}>
              {canAssignToOthers ? '👑 Super Admin' : '👤 Şəxsi Görünüş'}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {canAssignToOthers 
              ? 'Bütün istifadəçilərin tapşırıqlarını görə və idarə edə bilərsiniz'
              : 'Yalnız sizə təyin edilmiş tapşırıqları görə bilərsiniz'
            }
            {!canAssignToOthers && (
              <span className="block text-sm text-blue-600 mt-1">
                💡 Siz yalnız özünüz üçün tapşırıq yarada bilərsiniz
              </span>
            )}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <ExportButton 
            data={exportData}
            filename="tapşırıqlar"
            columns={exportHeaders}
            isExport={isExport}
            setIsExport={setIsExport}
          />
          {canAddTask && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Tapşırıq
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClipboardDocumentListIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-blue-100 text-sm font-medium">
                {canAssignToOthers ? 'Ümumi Tapşırıqlar' : 'Mənim Tapşırıqlarım'}
              </p>
              <p className="text-2xl font-bold">{totalTasks}</p>
              <p className="text-blue-200 text-xs mt-1">Unikal tapşırıqlar</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-yellow-100 text-sm font-medium">Gözləmədə</p>
              <p className="text-2xl font-bold">{pendingTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Davam edir</p>
              <p className="text-2xl font-bold">{inProgressTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Tamamlandı</p>
              <p className="text-2xl font-bold">{completedTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-red-100 text-sm font-medium">Yüksək Prioritet</p>
              <p className="text-2xl font-bold">{highPriorityTasks}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Legend */}
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Prioritet Rəngləri:</h3>
            <div className="flex space-x-6">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Yüksək</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Orta</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-700">Az</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600 mb-1">💡 <strong>Qeyd:</strong></p>
            <p className="text-xs text-gray-500 max-w-xs">
              Bir tapşırıq həm başlama həm də bitiş tarixi varsa birdən çox gündə görünə bilər. 
              Statistikalar unikal tapşırıq sayını göstərir.
            </p>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white shadow-lg rounded-lg border border-gray-200">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {dayNames.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50">
              {day.slice(0, 3)}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date, index) => {
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = date.getTime() === today.getTime();
            const counts = getTaskCountsByPriority(date);
            const hasHover = hoveredDate && hoveredDate.getTime() === date.getTime();

            return (
                             <div
                 key={index}
                 className={`group relative p-3 h-32 border-r border-b border-gray-200 cursor-pointer transition-all duration-200 ${
                   isToday 
                     ? 'hover:bg-gradient-to-br hover:from-orange-200 hover:to-orange-300' 
                     : 'hover:bg-blue-50'
                 } ${
                   !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
                 } ${isToday ? 'bg-gradient-to-br from-orange-100 to-orange-200 border-2 border-orange-400 shadow-md' : ''}`}
                 onClick={() => handleDateClick(date)}
                 onMouseEnter={() => handleDateHover(date)}
                 onMouseLeave={() => setHoveredDate(null)}
               >
                                 <div className="flex justify-between items-start mb-2">
                   <div className="flex flex-col items-start">
                     <span className={`text-sm font-bold ${isToday ? 'text-orange-800 text-lg' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                       {date.getDate()}
                     </span>
                     {isToday && (
                       <span className="text-xs font-bold text-orange-700 bg-orange-300 px-1 rounded">
                         BUGÜN
                       </span>
                     )}
                   </div>
                   <div className="flex items-center space-x-1">
                     {counts.total > 0 && (
                       <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                         {counts.total}
                       </span>
                     )}
                     {canAddTask && (
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           // Ensure we pass a valid Date object
                           if (date instanceof Date) {
                             handleAdd(date);
                           } else {
                             console.error('Invalid date object:', date);
                             handleAdd();
                           }
                         }}
                         className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-green-500 hover:bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold ${
                           isCurrentMonth ? '' : 'opacity-50'
                         }`}
                         title="Bu gün üçün tapşırıq əlavə et"
                       >
                         +
                       </button>
                     )}
                   </div>
                 </div>

                {/* Priority indicators */}
                <div className="space-y-1">
                  {counts.overdue > 0 && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-700 rounded-full mr-1 animate-pulse"></div>
                      <span className="text-xs text-red-700 font-bold">{counts.overdue}</span>
                    </div>
                  )}
                  {counts.high > 0 && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                      <span className="text-xs text-gray-600">{counts.high}</span>
                    </div>
                  )}
                  {counts.medium > 0 && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                      <span className="text-xs text-gray-600">{counts.medium}</span>
                    </div>
                  )}
                  {counts.low > 0 && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                      <span className="text-xs text-gray-600">{counts.low}</span>
                    </div>
                  )}
                </div>

                {/* Hover tooltip */}
                {hasHover && counts.total > 0 && (
                                     <div className="absolute top-full left-0 z-10 mt-1 p-2 bg-black text-white text-xs rounded shadow-lg min-w-max">
                     {date.getDate()} {monthNames[date.getMonth()]} - {counts.total} tapşırıq
                     {counts.overdue > 0 && <div>⚠️ {counts.overdue} vaxtı keçmiş</div>}
                     {counts.high > 0 && <div>🔴 {counts.high} yüksək</div>}
                     {counts.medium > 0 && <div>🟡 {counts.medium} orta</div>}
                     {counts.low > 0 && <div>🟢 {counts.low} az</div>}
                   </div>
                )}
              </div>
            );
          })}
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-16">
            <Audio
              height="80"
              width="80"
              radius="9"
              color="blue"
              ariaLabel="loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Yeni Tapşırıq Əlavə Et</h3>
                                 <button
                   onClick={() => {
                     setIsModalOpen(false);
                     setSelectedDate(null);
                   }}
                   className="text-gray-400 hover:text-gray-600"
                 >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Başlıq *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      maxLength={255}
                      placeholder="Tapşırıq başlığını daxil edin..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Təsvir</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Tapşırıq təsvirini daxil edin..."
                    />
                  </div>

                  {canAssignToOthers ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Təyin edilən *</label>
                      <select
                        value={formData.assigned_to}
                        onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">İstifadəçi seçin</option>
                        {usersData?.data?.filter(u => u.is_active).map(user => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Təyin edilən</label>
                      <div className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700">
                        {user?.first_name} {user?.last_name} (Siz)
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prioritet</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Prioritet seçin</option>
                      <option value="low">Az</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksək</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Başlama tarixi</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bitmə tarixi *</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Qeydlər</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Əlavə qeydlər..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                                     <button
                     type="button"
                     onClick={() => {
                       setIsModalOpen(false);
                       setSelectedDate(null);
                     }}
                     className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                   >
                     Ləğv Et
                   </button>
                  <button
                    type="submit"
                    disabled={createIsLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {createIsLoading ? 'Gözləyin...' : 'Əlavə Et'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Day Tasks Modal */}
      {isDayModalOpen && selectedDate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedDate.getDate()} {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()} - Tapşırıqlar
                </h3>
                <button
                  onClick={() => setIsDayModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {dayTasks.length > 0 ? (
                  dayTasks.map((task) => (
                    <div key={task.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-sm font-semibold text-gray-900">{task.title}</h4>
                            {task.priority && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getPriorityColor(task.priority)}`}>
                                {getPriorityText(task.priority)}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status)}`}>
                              {getStatusText(task.status)}
                            </span>
                            {isTaskOverdue(task.due_date) && task.status !== 'completed' && task.status !== 'cancelled' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold text-white bg-red-600 border border-red-700 shadow-sm">
                                VAXTİ KEÇMİŞ
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                          )}
                                                     <div className="flex items-center space-x-4 text-xs text-gray-500">
                             <span>👤 {task.assigned_user?.first_name} {task.assigned_user?.last_name}
                               {task.assigned_user?.id === user?.id && ' (Siz)'}
                             </span>
                             {task.start_date && (
                               <span>📅 Başlama: {new Date(task.start_date).toLocaleDateString('az-AZ')}</span>
                             )}
                             <span>⏰ Bitmə: {new Date(task.due_date).toLocaleDateString('az-AZ')}</span>
                           </div>
                        </div>
                        <div className="flex space-x-2">
                          {canEditTask && (
                            <button
                              onClick={() => handleEdit(task)}
                              className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                              title="Redaktə et"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteTask && (
                            <button
                              onClick={() => handleDelete(task.id)}
                              className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-colors"
                              title="Sil"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">Bu gün üçün tapşırıq yoxdur</p>
                                         <button
                       onClick={() => {
                         setIsDayModalOpen(false);
                         handleAdd(selectedDate);
                       }}
                       className="mt-3 inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                     >
                       <PlusIcon className="h-4 w-4 mr-1" />
                       Tapşırıq əlavə et
                     </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setIsDayModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
                                 <button
                   onClick={() => {
                     setIsDayModalOpen(false);
                     handleAdd(selectedDate);
                   }}
                   className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                 >
                   Yeni Tapşırıq
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {isTaskModalOpen && editingTask && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Tapşırığı Redaktə Et</h3>
                <button
                  onClick={() => setIsTaskModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Başlıq *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      maxLength={255}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Təsvir</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                                     {canAssignToOthers ? (
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">Təyin edilən *</label>
                       <select
                         value={formData.assigned_to}
                         onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                         className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                         required
                       >
                         <option value="">İstifadəçi seçin</option>
                         {usersData?.data?.filter(u => u.is_active).map(user => (
                           <option key={user.id} value={user.id}>
                             {user.first_name} {user.last_name}
                           </option>
                         ))}
                       </select>
                     </div>
                   ) : (
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">Təyin edilən</label>
                       <div className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700">
                         {user?.first_name} {user?.last_name} (Siz)
                       </div>
                     </div>
                   )}

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="pending">Gözləmədə</option>
                      <option value="in_progress">Davam edir</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="cancelled">Ləğv edildi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prioritet</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Prioritet seçin</option>
                      <option value="low">Az</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksək</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Başlama tarixi</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bitmə tarixi *</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Qeydlər</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setIsTaskModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    disabled={updateIsLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {updateIsLoading ? 'Gözləyin...' : 'Yenilə'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 